<template>
  <TabbedBox>
    <template v-slot:tablist>
      <Tab tabid="tab-production" :first="true">
        <i class="far fa-code-branch"></i> Production Station Apps
      </Tab>
    </template>
    <template v-slot:tabcontent>
      <TabPage tabid="tab-production" :first="true">
        <div class="row">
          <div class="col-lg-12">
            <ReportFilterPopover
              class="float-right mr-2"
              headerTitle="Production Filter"
              :showApplyButton="true"
              :showResetButton="true"
              :filterActive="hasFilterActive"
              @apply="applyProductionFilter"
              @reset="resetProductionFilter">
              <template v-slot:popover>
                <ReportFilterGroup label="Account Location">
                  <FormRow label="Account Locations">
                    <FormMultiSelect
                      :options="accountLocationLookup"
                      v-model="editingProductionFilter.accountLocationIdList" />
                  </FormRow>               
                </ReportFilterGroup>
                <FormHorizontalRule />
                <ReportFilterGroup label="Other Filters">
                  Hide Empty Stages
                  <FormSwitch booleanMode v-model="editingProductionFilter.hideEmptyStages">Hide Empty Stages</FormSwitch>
                </ReportFilterGroup>
              </template>
              <template v-slot:filterDisplay>
                <div v-if="hasFilterActive">
                  <i class="far fa-map-marker-alt mr-2"></i> {{filteredLocationListDisplay}}
                </div>
                <div v-if="hasFilterActive && editingProductionFilter.hideEmptyStages">
                  <i class="far fa-eye-slash mr-2"></i> Hiding Empty Stages
                </div>
              </template>
            </ReportFilterPopover>

            <h3>Production Tree</h3>
          </div>
        </div>
        <StaticDataTable class="static-table-list production-tree-list" :hover="true" :striped="false" @sortUpdated="productionTree.sortOrder = $event" :key="productionTree.updateKey">
          <template v-slot:header>
            <tr>
              <th>Name</th>
              <th>Category / Type</th>
              <th>Location</th>
              <th>Scanning Options</th>
              <th class="text-center">Active</th>
              <th>View</th>
            </tr>
          </template>
          <template v-slot:body>
            <template v-for="(stage, stageIndex) in productionTree.stageList">
              <tr v-if="stageIndex > 0" class="order-item-spacer" :key="`item-${stageIndex}-spacer`">
                <td colspan="6"> </td>
              </tr>

              <tr :key="stage.id" :data-sort-id="stage.id" class="static-table-sortable production-tree-row" :style="getTreeGroupRowStyles(stage)">
                <td class="font-weight-bold">
                  <LinkProductionStage :id="stage.id" :name="`Stage - ${stage.name}`" :newTab="false" />
                  <LabelBadge v-if="stage.isExternal" labelClass="label-production-stage-external">External</LabelBadge>
                </td>
                <td>{{stage.category | formatLookupName(ProductionStageCategoryTypeLookup)}}</td>
                <td> </td>
                <td> </td>
                <td class="text-center">
                  <IconBoolean :value="stage.isActive" :displayFalse="true" />
                </td>
                <td> </td>
              </tr>
              <template v-if="!productionTree.editing">
                <tr v-for="station in stage.productionStations" class="production-tree-row" :key="`stage-${stage.id}-station-${station.id}`" :style="getTreeGroupRowStyles(stage)">
                  <td>
                    <i class="fas fa-caret-right mr-2 ml-3"></i>
                    <LinkProductionStation :id="station.id" :name="station.name" :newTab="false" />
                    <template v-if="station.secondaryStation">
                      <i class="fas fa-long-arrow-right mr-3 ml-3"></i>
                      <span class="font-italic">{{station.secondaryStation.name}}</span>
                    </template>
                    <LabelBadge labelClass="label-production-maintenance" class="ml-3" v-if="station.isMaintenanceMode"><i class="fas fa-wrench mr-1"></i> In Maintenance</LabelBadge>
                  </td>
                  <td>
                    {{station.type | formatLookupName(ProductionStationTypeLookup)}}
                  </td>
                  <td>
                    {{station.accountLocation ? station.accountLocation.name : '-'}}
                  </td>
                  <td>
                    <LabelBadge labelClass="label-production-scanning-in mr-1" v-if="station.isScanInAvailable"><i class="fas fa-arrow-right mr-1"></i> Scan In</LabelBadge>
                    <LabelBadge labelClass="label-production-scanning-out" v-if="station.isScanOutAvailable"><i class="fas fa-check-circle mr-1"></i> Scan Out</LabelBadge>
                  </td>
                  <td class="text-center">
                    <IconBoolean :value="station.isActive" :displayFalse="true" />
                  </td>
                  <td>
                    <LinkButton 
                      v-if="userHasReadPermission(PermissionTypes.ProductionPermissionTypes.productionStationApp)"
                      :href="`/apps/glass-production-station/${station.id}/stage/${stage.id}`"
                      :newTab="true"
                      colourClass="btn-default"
                      icon="fa-desktop">
                      Station View
                    </LinkButton>
                  </td>
                </tr>                  
              </template>
            </template>
          </template>
        </StaticDataTable>
      </TabPage>
    </template>
  </TabbedBox>
</template>

<script>
import _ from 'lodash'
import { mapState } from "vuex"

import form from '@/mixins/form'
import DataDisplayMixin from '@/mixins/dataDisplay'

import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportFilterGroup from '@/components/reporting/reportFilterGroup'

import { ProductionStageCategoryTypeLookup, ProductionStationTypeLookup } from '@/store/types'

export default {
  name: 'TasksConfiguration',
  mixins: [form, DataDisplayMixin],
  components: {
    ReportFilterPopover,
    ReportFilterGroup
  },
  data: () => {
    return {
      productionTree: {
        updateKey: 0,
        sortOrder: [],
        form: {},
        stageList: [],
        stationList: []
      },

      defaultProductionFilter: {    
        accountLocationIdList: [],
        hideEmptyStages: false
      },
      editingProductionFilter: {},
      appliedProductionFilter: {},
    }
  },
  created() {
    this.ProductionStageCategoryTypeLookup = ProductionStageCategoryTypeLookup
    this.ProductionStationTypeLookup = ProductionStationTypeLookup
  },
  mounted() {
    this.editingProductionFilter = _.clone(this.defaultProductionFilter)
    this.appliedProductionFilter = _.clone(this.defaultProductionFilter)

    this.requestData()
  },
  computed: {
    ...mapState({
      accountLocations: state => state.account.accountLocations
    }),
    hasFilterActive() {
      return this.appliedProductionFilter.accountLocationIdList && this.appliedProductionFilter.accountLocationIdList.length > 0
    },
    filteredLocationListDisplay() {
      return this.formatMultiSelectLookupName(this.appliedProductionFilter.accountLocationIdList, this.accountLocationLookup)
    },
    accountLocationLookup() {
      return (this.accountLocations || []).filter(location => location.isActive).map(location => {
        return {
          name: location.name,
          value: location.id
        }
      })
    },    
  },
  methods: {
    reloadData() {
      this.requestData()
    },
    requestData() {
      this.pageLoadData(Promise.all([
        this.$apiService.productionService.getProductionStageList(),
        this.$apiService.productionService.getProductionStationList(),
      ]))
      .then(results => {
        this.productionTree.stageList = results[0].data
        this.productionTree.stationList = results[1].data

        if(this.appliedProductionFilter.accountLocationIdList && this.appliedProductionFilter.accountLocationIdList.length > 0) {

          // Filter stations based on selected account locations
          this.productionTree.stageList.forEach(stage => {
            if(stage.productionStations) {
              stage.productionStations = stage.productionStations.filter(station => this.appliedProductionFilter.accountLocationIdList.includes(station.accountLocationId))
            }
          })
        }

        // If hiding empty stages, filter out stages with no stations
        if(this.appliedProductionFilter.hideEmptyStages) {
          this.productionTree.stageList = this.productionTree.stageList.filter(stage => {
            return stage.productionStations && stage.productionStations.length > 0
          })
        }

        this.productionTree.sortOrder = this.productionTree.stageList.map(stage => stage.id)
      })
      .catch(err => this.handlePageLoadError(err))
    },

    // Production Tree
    scanTypeDescription(station) {
      if(station.isScanInAvailable && station.isScanOutAvailable)
        return 'In and Out'
      if(station.isScanInAvailable)
        return 'In Only'
      if(station.isScanOutAvailable)
        return 'Out Only'
      return 'None'
    },

    getTreeGroupRowStyles(group) {
      return {
        'border-left': `4px solid ${group.highlightColour}`
      }
    },
    applyProductionFilter() {
      this.appliedProductionFilter = _.clone(this.editingProductionFilter)

      this.reloadData()
    },
    resetProductionFilter() {
      this.editingProductionFilter = _.clone(this.defaultProductionFilter)
      this.appliedProductionFilter = _.clone(this.defaultProductionFilter)

      this.reloadData()
    },
  }
}

</script>